import { FC, useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { Users, Briefcase, Calendar, DollarSign, Clock } from 'lucide-react';
import { AdminLayout } from './AdminLayout';
import { StatsCard } from '../../components/admin/StatsCard';
import { StatusBadge } from '../../components/admin/StatusBadge';
import { adminApi } from '../../api/adminApi';
import { AdminDashboardSkeleton } from '../../components/skeletons';
import { formatCurrencyAmount } from '../../utils/currency';
import type { DashboardStats, AdminBooking } from '../../types/admin.types';

type PageState = 'loading' | 'loaded' | 'error';

function formatDate(dateString: string): string {
  const date = new Date(dateString);
  return date.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
}

export const AdminDashboardPage: FC = () => {
  const [pageState, setPageState] = useState<PageState>('loading');
  const [stats, setStats] = useState<DashboardStats | null>(null);
  const [recentBookings, setRecentBookings] = useState<AdminBooking[]>([]);
  const [errorMessage, setErrorMessage] = useState('');

  const fetchDashboardData = useCallback(async () => {
    try {
      setPageState('loading');

      // Fetch stats and recent bookings in parallel
      const [statsData, bookingsResponse] = await Promise.all([
        adminApi.getDashboardStats(),
        adminApi.listBookings({ limit: 5, sortBy: 'createdAt', sortOrder: 'desc' }),
      ]);

      setStats(statsData);
      setRecentBookings(bookingsResponse.data);
      setPageState('loaded');
    } catch (error) {
      setErrorMessage(error instanceof Error ? error.message : 'Failed to load dashboard data');
      setPageState('error');
    }
  }, []);

  useEffect(() => {
    fetchDashboardData();
  }, [fetchDashboardData]);

  if (pageState === 'loading') {
    return (
      <AdminLayout>
        <AdminDashboardSkeleton />
      </AdminLayout>
    );
  }

  if (pageState === 'error') {
    return (
      <AdminLayout>
        <div className="flex h-96 items-center justify-center">
          <div className="text-center">
            <div className="text-red-500 text-5xl mb-4">!</div>
            <h2 className="text-xl font-bold text-gray-900 dark:text-slate-100 mb-2">Error Loading Dashboard</h2>
            <p className="text-gray-600 dark:text-slate-400 mb-4">{errorMessage}</p>
            <button
              onClick={fetchDashboardData}
              className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
            >
              Try Again
            </button>
          </div>
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div className="space-y-6">
        {/* Page Header */}
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-slate-100">Overview</h1>
          <p className="mt-1 text-gray-500 dark:text-slate-400">Welcome to your admin dashboard</p>
        </div>

        {/* Stats Grid */}
        <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
          <StatsCard
            title="Total Therapists"
            value={stats?.therapists.total || 0}
            description={`${stats?.therapists.active || 0} active`}
            icon={<Users className="h-6 w-6" />}
          />
          <StatsCard
            title="Total Services"
            value={stats?.services.total || 0}
            description={`${stats?.services.active || 0} active`}
            icon={<Briefcase className="h-6 w-6" />}
          />
          <StatsCard
            title="Bookings Today"
            value={stats?.bookings.today || 0}
            description={`${stats?.bookings.confirmed || 0} confirmed total`}
            icon={<Calendar className="h-6 w-6" />}
          />
          <StatsCard
            title="Monthly Revenue"
            value={formatCurrencyAmount(stats?.revenue.monthly || 0)}
            icon={<DollarSign className="h-6 w-6" />}
          />
        </div>

        {/* Quick Actions */}
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
          <Link
            to="/admin/therapists"
            className="flex items-center gap-3 rounded-lg border border-gray-200 dark:border-slate-700 bg-white dark:bg-slate-800 p-4 shadow-sm transition-all hover:border-blue-300 dark:hover:border-blue-700 hover:shadow-md"
          >
            <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-blue-50 dark:bg-blue-900/30">
              <Users className="h-5 w-5 text-blue-600" />
            </div>
            <div>
              <p className="font-medium text-gray-900 dark:text-slate-100">Manage Therapists</p>
              <p className="text-sm text-gray-500 dark:text-slate-400">Add, edit, or remove</p>
            </div>
          </Link>

          <Link
            to="/admin/services"
            className="flex items-center gap-3 rounded-lg border border-gray-200 dark:border-slate-700 bg-white dark:bg-slate-800 p-4 shadow-sm transition-all hover:border-blue-300 dark:hover:border-blue-700 hover:shadow-md"
          >
            <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-green-50 dark:bg-green-900/30">
              <Briefcase className="h-5 w-5 text-green-600" />
            </div>
            <div>
              <p className="font-medium text-gray-900 dark:text-slate-100">Manage Services</p>
              <p className="text-sm text-gray-500 dark:text-slate-400">Configure offerings</p>
            </div>
          </Link>

          <Link
            to="/admin/bookings"
            className="flex items-center gap-3 rounded-lg border border-gray-200 dark:border-slate-700 bg-white dark:bg-slate-800 p-4 shadow-sm transition-all hover:border-blue-300 dark:hover:border-blue-700 hover:shadow-md"
          >
            <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-purple-50 dark:bg-purple-900/30">
              <Calendar className="h-5 w-5 text-purple-600" />
            </div>
            <div>
              <p className="font-medium text-gray-900 dark:text-slate-100">View Bookings</p>
              <p className="text-sm text-gray-500 dark:text-slate-400">Manage appointments</p>
            </div>
          </Link>

          <Link
            to="/admin/bookings?status=PENDING_PAYMENT"
            className="flex items-center gap-3 rounded-lg border border-gray-200 dark:border-slate-700 bg-white dark:bg-slate-800 p-4 shadow-sm transition-all hover:border-blue-300 dark:hover:border-blue-700 hover:shadow-md"
          >
            <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-yellow-50 dark:bg-yellow-900/30">
              <Clock className="h-5 w-5 text-yellow-600" />
            </div>
            <div>
              <p className="font-medium text-gray-900 dark:text-slate-100">Pending Bookings</p>
              <p className="text-sm text-gray-500 dark:text-slate-400">Awaiting payment</p>
            </div>
          </Link>
        </div>

        {/* Recent Bookings */}
        <div className="rounded-lg border border-gray-200 dark:border-slate-700 bg-white dark:bg-slate-800 shadow-sm">
          <div className="flex items-center justify-between border-b border-gray-200 dark:border-slate-700 px-6 py-4">
            <h3 className="font-semibold text-gray-900 dark:text-slate-100">Recent Bookings</h3>
            <Link
              to="/admin/bookings"
              className="text-sm font-medium text-blue-600 hover:text-blue-800"
            >
              View all
            </Link>
          </div>
          <div className="divide-y divide-gray-200 dark:divide-slate-700">
            {recentBookings.length === 0 ? (
              <div className="px-6 py-8 text-center text-gray-500 dark:text-slate-400">No bookings yet</div>
            ) : (
              recentBookings.map((booking) => (
                <div
                  key={booking.id}
                  className="flex items-center justify-between px-6 py-4 hover:bg-gray-50 dark:hover:bg-slate-700"
                >
                  <div className="flex items-center gap-4">
                    <div className="flex h-10 w-10 items-center justify-center rounded-full bg-gray-100 dark:bg-slate-700">
                      <Calendar className="h-5 w-5 text-gray-600 dark:text-slate-400" />
                    </div>
                    <div>
                      <p className="font-medium text-gray-900 dark:text-slate-100">{booking.service.name}</p>
                      <p className="text-sm text-gray-500 dark:text-slate-400">
                        {booking.user.name} with {booking.therapist.user.name}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-4">
                    <div className="text-right">
                      <p className="text-sm font-medium text-gray-900 dark:text-slate-100">
                        {formatCurrencyAmount(booking.totalAmount)}
                      </p>
                      <p className="text-xs text-gray-500 dark:text-slate-400">{formatDate(booking.startTime)}</p>
                    </div>
                    <StatusBadge status={booking.status} />
                  </div>
                </div>
              ))
            )}
          </div>
        </div>
      </div>
    </AdminLayout>
  );
};

export default AdminDashboardPage;
